import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { StatusCode } from 'grpc-web';
import { Google, ExclamationTriangleFill } from 'react-bootstrap-icons';
import Sidebar, { BrandMark } from './Sidebar';
import { AdminQuestionServiceClient } from '../protos/adminbackendproto/AdminQuestionServiceServiceClientPb';
import { AdminNewsletterServiceClient } from '../protos/adminbackendproto/AdminNewsletterServiceServiceClientPb';
import { CheckLoginRequest } from '../protos/adminbackendproto/AdminQuestionService_pb';
import AuthInterceptor from '../interceptors/AuthInterceptor';

interface LoginProps {
    children: ((result: CheckLoginResult) => ReactNode)
}

interface CheckLoginResult {
    client: AdminQuestionServiceClient,
    newsletterClient: AdminNewsletterServiceClient,
    hasEditAccess: Boolean
}

export default function LoginWrapper(props: LoginProps) {
    // Only show the splash when there's a stored token to verify; otherwise go
    // straight to the sign-in card.
    const [isLoadingLogin, setIsLoadingLogin] = useState<Boolean>(() => localStorage.getItem("login-token") !== null);
    const [client, setClient] = useState<CheckLoginResult>();
    const [error, setError] = useState<String>();
    // Read inside the interceptor callback, which is created once per sign-in;
    // state captured there would always be the value from that moment.
    const signedIn = useRef(false);

    const login = useGoogleLogin({
        onSuccess: (resp) => {
            checkLoginWithServer(resp.access_token);
        },
        onError: (error) => {
            setError("There was a problem logging in with Google. Try logging in again.");
            console.log('Login Failed:', error)
        }
    });

    useEffect(() => {
        const token = localStorage.getItem("login-token")
        if (token !== null) {
            checkLoginWithServer(token, true);
        }
    }, [])

    function checkLoginWithServer(token: string, fromStorage: boolean = false) {
        setIsLoadingLogin(true);
        const authInterceptor = new AuthInterceptor(token, () => {
            // Google access tokens last an hour, and the admin backend checks
            // the token with Google on every call, so this fires both for an
            // expired session and for an account without access.
            localStorage.removeItem("login-token");
            if (signedIn.current || fromStorage) {
                signedIn.current = false;
                setClient(undefined);
                setError("Your session expired. Sign in again to continue.");
            } else {
                setError("This Google account is not authorized to view this page. Try logging in with another account or request access.");
            }
        });
        let host = window.location.host
        let adminService = new AdminQuestionServiceClient(
            window.location.protocol + '//' + host + '/api', undefined, {
            unaryInterceptors: [authInterceptor],
        });
        let newsletterService = new AdminNewsletterServiceClient(
            window.location.protocol + '//' + host + '/api', undefined, {
            unaryInterceptors: [authInterceptor],
        });
        let request = new CheckLoginRequest()
        adminService.checkLogin(request, {}).then((response) => {
            signedIn.current = true;
            setError(undefined);
            setClient({
                client: adminService,
                newsletterClient: newsletterService,
                hasEditAccess: response.getHasEditAccess()
            });
            localStorage.setItem("login-token", token);
        }).catch(err => {
            console.log("Failed to check login", err);
            localStorage.removeItem("login-token");
            if (err.code !== StatusCode.PERMISSION_DENIED) {  // that case set its own message
                setError("There was a problem authenticating this user. Try logging in again.");
            }
            return;
        }).finally(() => {
            setIsLoadingLogin(false);
        });
    }

    if (client) {
        return <div className="app-shell">
            <Sidebar hasEditAccess={client.hasEditAccess} onLogout={() => {
                signedIn.current = false
                localStorage.removeItem("login-token")
                setClient(undefined)
                setError(undefined)
                googleLogout()
            }} />
            <main className="main">
                {props.children(client)}
            </main>
        </div>
    }

    return <div className="login-screen">
        <div className="panel login-card">
            <div className="brand">
                <BrandMark />
                <span className="brand-name">Dwellable</span>
                <span className="brand-tag">Admin</span>
            </div>
            {isLoadingLogin
                ? <div className="d-flex flex-column align-items-center gap-3 py-3 text-secondary">
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Checking your session…
                </div>
                : <>
                    <h1>Welcome back</h1>
                    <p>Sign in with your Google account to continue.</p>
                    {error && <div className="banner danger mb-3 text-start">
                        <ExclamationTriangleFill className="flex-shrink-0 mt-1" />
                        <span>{error}</span>
                    </div>}
                    <button className="btn btn-accent w-100 py-2" onClick={() => login()}>
                        <Google /> Sign in with Google
                    </button>
                </>}
        </div>
    </div>
}
